import React, { useState, useEffect } from 'react';
import {
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Select,
    VStack,
    useToast,
    HStack,
    Flex,
} from '@chakra-ui/react';
import api from '../../../auth/apiClient';
import * as XLSX from 'xlsx-js-style';
import { RainfallReportResponse } from '../../../types/rainfall';
import { trackDownload } from '../../../activity';

interface RainfallReportPopupProps {
    isOpen: boolean;
    onClose: () => void;
}

interface FilterOptions {
    companies: { id: number; name: string }[];
    estates: { id: number; name: string; company_id: number }[];
    divisis: { id: number; name: string; estate_id: number }[];
}

interface RainfallReportPopupProps {
    isOpen: boolean;
    onClose: () => void;
}

const RainfallReportPopup: React.FC<RainfallReportPopupProps> = ({ isOpen, onClose }) => {
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth() + 1);
    const [selectedCompany, setSelectedCompany] = useState<string>('');
    const [selectedEstate, setSelectedEstate] = useState<string>('');
    const [selectedDivision, setSelectedDivision] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState<FilterOptions>({
        companies: [],
        estates: [],
        divisis: []
    });
    const toast = useToast();

    // Generate years from 2020 to 2030
    const years = Array.from({ length: 11 }, (_, i) => 2020 + i);

    // Generate months
    const months = [
        { value: 1, label: 'Januari' },
        { value: 2, label: 'Februari' },
        { value: 3, label: 'Maret' },
        { value: 4, label: 'April' },
        { value: 5, label: 'Mei' },
        { value: 6, label: 'Juni' },
        { value: 7, label: 'Juli' },
        { value: 8, label: 'Agustus' },
        { value: 9, label: 'September' },
        { value: 10, label: 'Oktober' },
        { value: 11, label: 'November' },
        { value: 12, label: 'Desember' },
    ];

    // Fetch options from API
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const estatesResponse = await api.get('/inspeksi/web/estate-db');
                const optionsResponse = await api.get('/inspeksi/options');

                setOptions({
                    companies: optionsResponse.data.companies || [],
                    estates: estatesResponse.data || [],
                    divisis: optionsResponse.data.divisis || []
                });
            } catch (error) {
                console.error("Error fetching options", error);
            }
        };

        if (isOpen) {
            fetchOptions();
        }
    }, [isOpen]);

    // Reset estate and division when company changes
    useEffect(() => {
        setSelectedEstate('');
        setSelectedDivision('');
    }, [selectedCompany]);

    // Reset division when estate changes
    useEffect(() => {
        setSelectedDivision('');
    }, [selectedEstate]);

    // Get filtered estates based on selected company
    const filteredEstates = options.estates.filter(estate =>
        !selectedCompany || estate.company_id === parseInt(selectedCompany)
    );

    // Get filtered divisions based on selected estate
    const filteredDivisions = options.divisis.filter(division =>
        !selectedEstate || division.estate_id === parseInt(selectedEstate)
    );

    // Get company name for header
    const getCompanyName = () => {
        if (!selectedCompany) return 'PT.GOLDEN BLOSSOM SUMATRA';
        const company = options.companies.find(c => c.id === parseInt(selectedCompany));
        return company ? company.name.toUpperCase() : 'PT.GOLDEN BLOSSOM SUMATRA';
    };

    // Get company average label
    const getCompanyAverageLabel = () => {
        if (!selectedCompany) return 'RATA-RATA GBS';
        const companyId = parseInt(selectedCompany);
        return companyId === 1 ? 'RATA-RATA GBS' : 'RATA-RATA LKK';
    };

    const handleExport = async () => {
        if (!selectedYear || !selectedMonth || !selectedCompany) {
            toast({
                title: 'Error',
                description: 'Silakan pilih tahun, bulan, dan company',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsLoading(true);
        try {
            // Build API URL
            const baseUrl = '/report/rainfall/';
            const params: any = {
                year: selectedYear,
                month: selectedMonth,
                company_id: parseInt(selectedCompany)
            };

            if (selectedEstate) params.estate_id = parseInt(selectedEstate);
            if (selectedDivision) params.divisi_id = parseInt(selectedDivision);

            console.log('Request URL & params:', baseUrl, params);

            const response = await api.get<RainfallReportResponse>(baseUrl, { params });
            const data = response.data;

            // Create Excel workbook
            const workbook = XLSX.utils.book_new();

            // Prepare data for Excel
            const excelData = prepareExcelData(data);

            // Create worksheet
            const worksheet = XLSX.utils.aoa_to_sheet(excelData);

            // Apply styling
            applyExcelStyling(worksheet, excelData.length);

            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Laporan Curah Hujan');

            // Generate filename
            const monthName = months.find(m => m.value === selectedMonth)?.label || selectedMonth.toString();
            const filename = `Laporan_Curah_Hujan_${monthName}_${selectedYear}.xlsx`;

            // Save file
            XLSX.writeFile(workbook, filename);
            trackDownload('Laporan Curah Hujan', { filename });

            toast({
                title: 'Berhasil',
                description: 'Laporan curah hujan berhasil diunduh',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            onClose();
        } catch (error) {
            console.error('Error exporting rainfall report:', error);
            toast({
                title: 'Error',
                description: 'Gagal mengunduh laporan curah hujan',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const createExcelHeaders = (daysInMonth: number) => {
        const monthName = months.find(m => m.value === selectedMonth)?.label.toUpperCase();
        const companyName = getCompanyName();

        // Create the header rows with new structure starting from row 4 (index 3)
        const headers = [
            [companyName], // Row 1
            ['LAPORAN HARIAN CURAH HUJAN'], // Row 2
            [`PERIODE ${monthName} ${selectedYear}`], // Row 3
            ['NO', 'ESTATE', 'DIVISI', 'LUAS (Ha)', 'CH (MM)', ...Array.from({ length: daysInMonth - 1 }, () => ''), 'TOTAL MM', 'HARI HUJAN', 'JUMLAH HARI TIDAK HUJAN'], // Row 4
            ['', '', '', '', 'TGL', ...Array.from({ length: daysInMonth - 1 }, () => ''), '', '', ''], // Row 5
            ['', '', '', '', ...Array.from({ length: daysInMonth }, (_, i) => i + 1), '', '', ''] // Row 6
        ];

        return headers;
    };

    const createDivisionRow = (division: any, daysInMonth: number, rowNumber: string | number, estateName: string, isFirstInEstate: boolean): any[] => {
        const dailyValues = division.daily_rainfall.slice(0, daysInMonth).map((d: any) => {
            const value = d.rainfall_mm;
            if (value === null || value === undefined || value === 0) return '-';
            return typeof value === 'number' ? parseFloat(value.toFixed(1)) : value;
        });

        return [
            isFirstInEstate ? rowNumber : '',
            isFirstInEstate ? estateName : '',
            division.division_name,
            division.total_planted_area ? parseFloat(division.total_planted_area.toFixed(1)) : '-',
            ...dailyValues,
            division.total_mm ? parseFloat(division.total_mm.toFixed(1)) : '-',
            division.hari_hujan || '-',
            division.jumlah_hari_tidak_hujan || '-'
        ];
    };

    const createEstateAverageRow = (estateAvg: any, daysInMonth: number, estateName: string): any[] => {
        const avgDailyValues = estateAvg.daily_rainfall_avg.slice(0, daysInMonth).map((d: any) => {
            const value = d.rainfall_mm;
            if (value === null || value === undefined || value === 0) return '-';
            return typeof value === 'number' ? parseFloat(value.toFixed(1)) : value;
        });

        return [
            'Rata-rata',
            '', // Kosongkan kolom B karena akan di-merge dengan kolom A
            '',
            estateAvg.avg_planted_area ? parseFloat(estateAvg.avg_planted_area.toFixed(1)) : '-',
            ...avgDailyValues,
            estateAvg.avg_total_mm ? parseFloat(estateAvg.avg_total_mm.toFixed(1)) : '-',
            estateAvg.avg_hari_hujan || '-',
            estateAvg.avg_jumlah_hari_tidak_hujan || '-'
        ];
    };

    const createCompanyAverageRow = (companyAvg: any, daysInMonth: number): any[] => {
        const companyAvgDailyValues = companyAvg.daily_rainfall_avg.slice(0, daysInMonth).map((d: any) => {
            const value = d.rainfall_mm;
            if (value === null || value === undefined || value === 0) return '-';
            return typeof value === 'number' ? parseFloat(value.toFixed(1)) : value;
        });
        const companyLabel = getCompanyAverageLabel();

        return [
            companyLabel,
            '',
            '',
            companyAvg.avg_planted_area ? parseFloat(companyAvg.avg_planted_area.toFixed(1)) : '-',
            ...companyAvgDailyValues,
            companyAvg.avg_total_mm ? parseFloat(companyAvg.avg_total_mm.toFixed(1)) : '-',
            companyAvg.avg_hari_hujan || '-',
            companyAvg.avg_jumlah_hari_tidak_hujan || '-'
        ];
    };

    const groupDivisionsByEstate = (divisions: any[]): Record<string, any[]> => {
        return divisions.reduce((groups, division) => {
            if (!groups[division.estate_name]) {
                groups[division.estate_name] = [];
            }
            groups[division.estate_name].push(division);
            return groups;
        }, {} as Record<string, any[]>);
    };

    const prepareExcelData = (data: RainfallReportResponse) => {
        const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
        const excelData: any[][] = [...createExcelHeaders(daysInMonth)];

        let rowNumber = 1;
        const estateGroups = groupDivisionsByEstate(data.division_reports);

        Object.entries(estateGroups).forEach(([estateName, divisions]: [string, any[]]) => {
            divisions.forEach((division: any, index: number) => {
                const row = createDivisionRow(division, daysInMonth, rowNumber, estateName, index === 0);
                excelData.push(row);
            });

            const estateAvg = data.estate_averages.find(e => e.estate_name === estateName);
            if (estateAvg) {
                const avgRow = createEstateAverageRow(estateAvg, daysInMonth, estateName);
                excelData.push(avgRow);
            }

            rowNumber++;
        });

        const companyAvgRow = createCompanyAverageRow(data.company_average, daysInMonth);
        excelData.push(companyAvgRow);

        return excelData;
    };

    const createColumnWidths = () => {
        const colWidths = [
            { wch: 4 },   // NO - narrower
            { wch: 21 },  // ESTATE - wider for estate names
            { wch: 10 },   // DIVISI
            { wch: 10 },  // LUAS (Ha) - wider for area values
        ];

        // Add widths for daily columns (1-31) - narrower to fit more columns
        for (let i = 0; i < 31; i++) {
            colWidths.push({ wch: 3.5 });
        }

        colWidths.push(
            { wch: 10 }, // TOTAL MM
            { wch: 10 },  // HARI HUJAN - shorter header
            { wch: 20 }   // JUMLAH HARI TIDAK HUJAN - shorter header
        );

        return colWidths;
    };

    const getBasicBorderStyle = () => ({
        border: {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
        }
    });

    const getTitleStyle = (fontSize: number, isBold = true) => ({
        font: { bold: isBold, sz: fontSize },
        alignment: { horizontal: 'left', vertical: 'center' } // Added vertical center alignment
    });

    const getHeaderStyle = (fontSize: number, isBold = true) => ({
        font: { bold: isBold, sz: fontSize },
        alignment: { horizontal: 'center', vertical: 'center' } // Added vertical center alignment
    });

    const getTableHeaderStyle = () => ({
        ...getHeaderStyle(10),
        ...getBasicBorderStyle(),
        fill: { fgColor: { rgb: 'B8CCE4' } } // Light blue color
    });

    const getDataCellStyle = (col: number, isAverage = false, isNumeric = false) => {
        // Apply right alignment for numeric columns (LUAS, daily values, TOTAL MM, HARI HUJAN, JUMLAH HARI TIDAK HUJAN)
        const horizontalAlign = isNumeric ? 'right' : 'center';

        return {
            ...getBasicBorderStyle(),
            alignment: {
                horizontal: horizontalAlign,
                vertical: 'center'
            },
            font: isAverage ? { bold: true, sz: 9 } : { sz: 9 },
            ...(isAverage && { fill: { fgColor: { rgb: 'B8CCE4' } } }) // Apply header color (light blue) to all columns in average rows
        };
    };

    const applyCellStyling = (worksheet: XLSX.WorkSheet, row: number, col: number, daysInMonth: number) => {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = worksheet[cellAddress];

        if (!cell) return;

        // Initialize cell style
        if (!cell.s) cell.s = {};

        // Apply styles based on row type
        if (row === 0) {
            // Company name header
            cell.s = {
                ...getTitleStyle(14, true)
            };
        } else if (row === 1) {
            // Report title "LAPORAN HARIAN CURAH HUJAN"
            cell.s = {
                ...getTitleStyle(12, true)
            };
        } else if (row === 2) {
            // Period
            cell.s = {
                ...getTitleStyle(11, true)
            };
        } else if (row >= 3 && row <= 5) {
            // Header rows (NO, ESTATE, DIVISI, LUAS, CH(MM), TGL, numbers 1-31, TOTAL MM, etc)
            cell.s = getTableHeaderStyle();
        } else if (row > 5) {
            // Data rows
            // Check if this row is an average row by looking at the first cell (column 0)
            const firstCellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
            const firstCell = worksheet[firstCellAddress];
            const isAverageRow = firstCell && firstCell.v && (
                firstCell.v.toString().includes('Rata-rata') ||
                firstCell.v.toString().includes('RATA-RATA')
            );

            // Determine if this is a numeric column
            const isNumeric = col >= 3; // LUAS (Ha) and all columns after are numeric
            cell.s = getDataCellStyle(col, isAverageRow, isNumeric);
        }
    };

    const applyExcelStyling = (worksheet: XLSX.WorkSheet, totalRows: number) => {
        const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();

        // Set column widths
        worksheet['!cols'] = createColumnWidths();

        // Merge cells for headers
        worksheet['!merges'] ??= [];

        // Merge company name across all columns (total columns = 4 base + daysInMonth + 3 last columns)
        const totalCols = 4 + daysInMonth + 3 - 1; // -1 because it's 0-indexed
        worksheet['!merges'].push({
            s: { r: 0, c: 0 },
            e: { r: 0, c: totalCols }
        });

        // Merge report title
        worksheet['!merges'].push({
            s: { r: 1, c: 0 },
            e: { r: 1, c: totalCols }
        });

        // Merge period
        worksheet['!merges'].push({
            s: { r: 2, c: 0 },
            e: { r: 2, c: totalCols }
        });

        // Header merges starting from row 4 (index 3)
        // NO header - merge from row 4-6 (index 3-5), column A (index 0)
        worksheet['!merges'].push({
            s: { r: 3, c: 0 },
            e: { r: 5, c: 0 }
        });

        // ESTATE header - merge from row 4-6 (index 3-5), column B (index 1)
        worksheet['!merges'].push({
            s: { r: 3, c: 1 },
            e: { r: 5, c: 1 }
        });

        // DIVISI header - merge from row 4-6 (index 3-5), column C (index 2)
        worksheet['!merges'].push({
            s: { r: 3, c: 2 },
            e: { r: 5, c: 2 }
        });

        // LUAS (Ha) header - merge from row 4-6 (index 3-5), column D (index 3)
        worksheet['!merges'].push({
            s: { r: 3, c: 3 },
            e: { r: 5, c: 3 }
        });

        // CH (MM) header - merge from row 4 (index 3), column E to AI (index 4 to 3+daysInMonth)
        worksheet['!merges'].push({
            s: { r: 3, c: 4 },
            e: { r: 3, c: 3 + daysInMonth }
        });

        // TGL header - merge from row 5 (index 4), column E to AI (index 4 to 3+daysInMonth)
        worksheet['!merges'].push({
            s: { r: 4, c: 4 },
            e: { r: 4, c: 3 + daysInMonth }
        });

        // TOTAL MM header - merge from row 4-6 (index 3-5), column AJ (index 4+daysInMonth)
        worksheet['!merges'].push({
            s: { r: 3, c: 4 + daysInMonth },
            e: { r: 5, c: 4 + daysInMonth }
        });

        // HARI HUJAN header - merge from row 4-6 (index 3-5), column AK (index 5+daysInMonth)
        worksheet['!merges'].push({
            s: { r: 3, c: 5 + daysInMonth },
            e: { r: 5, c: 5 + daysInMonth }
        });

        // JUMLAH HARI TIDAK HUJAN header - merge from row 4-6 (index 3-5), column AL (index 6+daysInMonth)
        worksheet['!merges'].push({
            s: { r: 3, c: 6 + daysInMonth },
            e: { r: 5, c: 6 + daysInMonth }
        });

        // Apply styles to cells
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');

        for (let row = range.s.r; row <= range.e.r; row++) {
            for (let col = range.s.c; col <= range.e.c; col++) {
                applyCellStyling(worksheet, row, col, daysInMonth);
            }
        }

        // Special handling for company average row - merge columns A-D for the company label
        const companyAvgRowIndex = totalRows - 1; // Last row
        worksheet['!merges'].push({
            s: { r: companyAvgRowIndex, c: 0 },
            e: { r: companyAvgRowIndex, c: 2 }
        });

        // Set alignment for company average merge (center horizontal and vertical)
        const companyAvgCellAddress = XLSX.utils.encode_cell({ r: companyAvgRowIndex, c: 0 });
        if (worksheet[companyAvgCellAddress]) {
            worksheet[companyAvgCellAddress].s = {
                ...worksheet[companyAvgCellAddress].s,
                alignment: { horizontal: 'center', vertical: 'center' }
            };
        }

        // Merge columns A-B for estate average rows (where column A is 'Rata-rata')
        for (let row = 6; row < totalRows - 1; row++) { // Start from data rows, exclude last row (company average)
            const firstCellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
            const firstCell = worksheet[firstCellAddress];
            if (firstCell && firstCell.v === 'Rata-rata') {
                worksheet['!merges'].push({
                    s: { r: row, c: 0 },
                    e: { r: row, c: 1 }
                });
            }
        }

        // Merge and align for estate groups (NO and ESTATE columns)
        let estateStartRow = -1;
        for (let row = 6; row < totalRows - 1; row++) { // Start from data rows, exclude last row (company average)
            const noCellAddress = XLSX.utils.encode_cell({ r: row, c: 0 });
            const estateCellAddress = XLSX.utils.encode_cell({ r: row, c: 1 });
            const noCell = worksheet[noCellAddress];
            const estateCell = worksheet[estateCellAddress];

            // If NO cell has value and is not 'Rata-rata', it's start of estate
            if (noCell && noCell.v && noCell.v !== 'Rata-rata' && estateStartRow === -1) {
                estateStartRow = row;
            }

            // If NO cell is 'Rata-rata', it's end of estate
            if (noCell && noCell.v === 'Rata-rata' && estateStartRow !== -1) {
                const estateEndRow = row - 1; // Before the average row

                // Merge NO column (A) from start to end
                if (estateStartRow < estateEndRow) {
                    worksheet['!merges'].push({
                        s: { r: estateStartRow, c: 0 },
                        e: { r: estateEndRow, c: 0 }
                    });

                    // Set alignment for NO merge
                    const noMergeCellAddress = XLSX.utils.encode_cell({ r: estateStartRow, c: 0 });
                    if (worksheet[noMergeCellAddress]) {
                        worksheet[noMergeCellAddress].s = {
                            ...worksheet[noMergeCellAddress].s,
                            alignment: { horizontal: 'center', vertical: 'center' }
                        };
                    }
                }

                // Merge ESTATE column (B) from start to end
                if (estateStartRow < estateEndRow) {
                    worksheet['!merges'].push({
                        s: { r: estateStartRow, c: 1 },
                        e: { r: estateEndRow, c: 1 }
                    });

                    // Set alignment for ESTATE merge
                    const estateMergeCellAddress = XLSX.utils.encode_cell({ r: estateStartRow, c: 1 });
                    if (worksheet[estateMergeCellAddress]) {
                        worksheet[estateMergeCellAddress].s = {
                            ...worksheet[estateMergeCellAddress].s,
                            alignment: { horizontal: 'center', vertical: 'center' }
                        };
                    }
                }

                // Reset for next estate
                estateStartRow = -1;
            }
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Unduh Laporan Curah Hujan</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    <VStack spacing={4}>
                        <HStack width="100%" spacing={4}>
                            <FormControl>
                                <FormLabel>Tahun</FormLabel>
                                <Select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                >
                                    {years.map(year => (
                                        <option key={year} value={year}>
                                            {year}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Bulan</FormLabel>
                                <Select
                                    value={selectedMonth}
                                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                >
                                    {months.map(month => (
                                        <option key={month.value} value={month.value}>
                                            {month.label}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                        </HStack>

                        <FormControl isRequired>
                            <FormLabel>Company</FormLabel>
                            <Select
                                placeholder="Pilih Company"
                                value={selectedCompany}
                                onChange={(e) => setSelectedCompany(e.target.value)}
                            >
                                {options.companies.map(company => (
                                    <option key={company.id} value={company.id}>
                                        {company.name}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Estate (Opsional)</FormLabel>
                            <Select
                                placeholder="Pilih Estate"
                                value={selectedEstate}
                                onChange={(e) => setSelectedEstate(e.target.value)}
                                disabled={!selectedCompany}
                            >
                                {filteredEstates.map(estate => (
                                    <option key={estate.id} value={estate.id}>
                                        {estate.name}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>Divisi (Opsional)</FormLabel>
                            <Select
                                placeholder="Pilih Divisi"
                                value={selectedDivision}
                                onChange={(e) => setSelectedDivision(e.target.value)}
                                disabled={!selectedEstate}
                            >
                                {filteredDivisions.map(division => (
                                    <option key={division.id} value={division.id}>
                                        {division.name}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <Flex width="100%" justifyContent="flex-end" gap={3}>
                            <Button variant="ghost" onClick={onClose}>
                                Batal
                            </Button>
                            <Button
                                colorScheme="blue"
                                onClick={handleExport}
                                isLoading={isLoading}
                                loadingText="Mengunduh..."
                            >
                                Unduh Excel
                            </Button>
                        </Flex>
                    </VStack>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default RainfallReportPopup;
